import LogList from "../utils/LogList";

/** Правила, задающие семантику комбинаторов (кроме Trace2) */
export const combinatorReduction = {
  restricted: {
    alt: [
      'QuoteOn', // standard precedence
      'CaseBool',
      // 'Trace',
      'Concat',
      // 'Quote',
      'Unquote',
      'Delay',
      'EvalDelay',
      'RemoveNulls',
      'Filter',
      'CrossJoin',
      'JoinOn',
      'Get',
    ],
  },
  to: 'Application'
};

export const memoization = {
  tau: {
    alt: [
      'memoize-external',
      'memoize-application',
      { mu: 'memoize-external' },
      { nu: 'memoize-external' },
      { xi: 'memoize-external' },
      { mu: 'memoize-application' },
      { nu: 'memoize-application' },
      { xi: 'memoize-application' },
    ],
  }
};

/**
 * Энергичный (рекурсивный) вызов standardReduction и добавление семантики ко всем основным синтаксическим конструкциям и комбинаторам.
 */
export const standardReductionRule = {
  alt: [
    'Delay', // higher precedence!
    'QuickBeta',
    { seq: [{ nu: { type: 'isExternal' } }, 'beta'] }, // подставляем по β-редукции, если в аргументе стоит в явном виде External-объект
    { seq: ['MultivariateFunctionCompile', 'never'] },
    { mu: 'standardReduction' },
    { nu: 'standardReduction' },
    // { LetRecLocal: 'standardReduction' },
    { Literal: 'standardReduction', reduceObjTypes: ['Object'], inlineObjTypes: ['Object', 'Number', 'String', 'LambdaJSON', 'Table'] },
    { Literal: 'standardReduction', reduceObjTypes: ['Process'], inlineObjTypes: ['Object', 'Number', 'String', 'Process', 'LambdaJSON'] },
    { Literal: 'standardReduction', reduceObjTypes: ['LambdaJSON'], inlineObjTypes: ['Object', 'Number', 'String', 'LambdaJSON'] },
    { Literal: 'standardReduction', reduceObjTypes: ['RefText'], inlineObjTypes: ['Object', 'String'] },
    { Literal: 'standardReduction', reduceObjTypes: ['Container'], inlineObjTypes: [] },
    combinatorReduction,
    'Exec',
    'RunDiagram',
    'RefText',

    // External
    'TableTransformation',
    // 'MultivariateFunctionCompile',
    'inline-external-process',
    { epsilon: 'standardReduction', objType: 'ArrayList' },
    { 'ArrayListCollapse': 'never', objType: 'Object', disallowObjTypes: ['Number', 'Boolean', 'String'] },
    { 'ArrayListCollapse': 'never', objType: 'Number', disallowObjTypes: ['Object', 'Boolean', 'String'] },
    { 'ArrayListCollapse': 'never', objType: 'Boolean', disallowObjTypes: ['Number', 'Object', 'String'] },
    { 'ArrayListCollapse': 'never', objType: 'String', disallowObjTypes: ['Number', 'Boolean', 'Object'] },

    // Application
    'FixedPoint',
    'MultivariateFunction-C',
    'Function',
    'beta-eager-on-static-objType',

    // LetRec
    'letrec-simplify',
    'letrec-empty',
    'letrec-expand-eager-on-static-objType',
    // 'letrec-substitute-eager-on-static-objType',
    { 'letrec-exec': 'id' },

    { mu: 'MapArray' },
    { nu: 'MapArray' },
    { Literal: 'MapArray', reduceObjTypes: ['Object'], inlineObjTypes: [] },
    { mu: 'Trace2' },
    { nu: 'Trace2' },
    'App',
    'Quote', // lowest precedence
  ],
};

/** Стандартная редукция: энергичная, со всеми стандартными правилами, кроме Trace2 и MapArray; не залезает внутрь функций и в тело letrec */
export const standardReductionSpec = {
  fixpoint: 'standardReduction',
  equal: {
    alt: [
      memoization,
      {
        max: 100,
        tau: {
          uniqId: 'standardReduction',
          disabled: false,
          validate: false,
          memoize: {
            nonRepetitive: 'strict',
            monotonicFV: true,
            check: {
              seq: [
                { not: memoization },
                standardReductionRule,
              ],
            },
          },
        },
      },
    ],
  },
};

const loglist = new LogList();

export const standardReductionStepByStepSpec = {
  max: 10000,
  tau: {
    log: {
      fixpoint: 'standardReduction',
      equal: standardReductionRule,
    },
    threshold: -1,
    label: null,
    // showTermStr: true,
    if: 'reduced',
    callback({ getTermStr }) {
      loglist.addRecord(getTermStr);
    },
  },
};

window.showLogList = () => loglist.show();

const failValidationRule = {
  fixpoint: 'red',
  equal: {
    alt: [
      { mu: 'red' },
      { nu: 'red' },
      { LetRecLocal: 'red' },
      { Literal: 'red', reduceObjTypes: ['Object'], inlineObjTypes: ['Object', 'Number', 'String'] },
      { Literal: 'red', reduceObjTypes: ['Process'], inlineObjTypes: ['Object', 'Number', 'String', 'Process'] },
      { epsilon: 'red', objType: 'ArrayList' },
      {
        seq: [
          { mu: { xi: 'id' } },
          { check: 'beta', shallReduce: true },
        ],
      }
    ],
  },
};

const mainLoopReductionSpec = (isStepByStep) => ({
  max: 100,
  tau: {
    alt: [
      isStepByStep ? standardReductionStepByStepSpec : standardReductionSpec,
      {
        log: { 'eager-once': () => ({ seq: ['letrec-expand-eager-on-static-objType', { opt: 'letrec-simplify' }] }) },
        label: 'letrec-expand-eager-on-static-objType out of order',
        threshold: -1,
        showTerm: true,
        if: 'reduced',
      },
      {
        log: {
          'eager-once': {
            seq: [
              'letrec-substitute-koca',
              { opt: 'letrec-simplify' },
              { opt: standardReductionSpec },
            ],
          },
        },
        label: 'letrec-substitute-koca out of order',
        threshold: -1,
        showTerm: true,
        if: 'reduced',
      },
      { 'eager-once': { log: 'MapArray', label: 'MapArray out of order', threshold: -1, showTerm: true, if: 'reduced' } },
      { 'eager-once': 'Trace2' },
    ],
  },
});

export const mainReductionStepByStepSpec = {
  seq: [
    { log: 'id', threshold: -1, callback({ getTermStr }) { loglist.addRecord(() => 'Run main reduction\n' + getTermStr()); } },
    // { opt: { alt: [{ nu: 'id-State' }, 'beta'] } },
    // { opt: preprocessState },
    // { opt: { structural: 'letrec-factorize' } },
    { opt: mainLoopReductionSpec(true) },
    { log: 'id', threshold: -1, callback() { loglist.addRecord(() => 'Finish the reduction'); } },
  ],
};

export const mainReductionSpec = mainLoopReductionSpec(false);

export default mainReductionSpec;
